// src/pages/Campaigns/SurveyGenie/PanelistManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Alert, Snackbar, CircularProgress,
    InputAdornment, Tooltip, FormControl, InputLabel, Select,
    MenuItem, Tabs, Tab, Avatar, LinearProgress, Divider
} from '@mui/material';
import {
    ArrowBack, Add, Delete, Edit, Search, FilterList, Person,
    Email, Phone, LocationOn, Cake, Work, School, AttachMoney,
    Star, Send, Refresh, Download, Upload, EmojiEvents, History
} from '@mui/icons-material';
import { PANELIST_STATUS, PANELIST_STATUS_LABELS } from './surveyEnums';

const PanelistManagement = () => {
    const navigate = useNavigate();
    
    const [panelists, setPanelists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [activeTab, setActiveTab] = useState(0);
    
    // Dialog states
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [rewardDialog, setRewardDialog] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [inviteDialog, setInviteDialog] = useState(false);
    const [selectedPanelist, setSelectedPanelist] = useState(null);
    
    // Form state
    const [panelistForm, setPanelistForm] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        demographics: {
            age: '',
            gender: '',
            location: '',
            occupation: '',
            education: '',
            income_range: ''
        }
    });
    
    const [rewardForm, setRewardForm] = useState({
        points: 0,
        reason: ''
    });

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        totalPoints: 0
    });

    const fetchPanelists = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page + 1,
                per_page: rowsPerPage
            });
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('status', statusFilter);
            
            const response = await fetch(`/api/panelists?${params}`);
            if (response.ok) {
                const data = await response.json();
                setPanelists(data.panelists || []);
                setTotalCount(data.total || 0);
                
                // Calculate stats
                const allPanelists = data.panelists || [];
                setStats({
                    total: data.total || 0,
                    active: allPanelists.filter(p => p.status === 'ACTIVE').length,
                    inactive: allPanelists.filter(p => p.status === 'INACTIVE').length,
                    totalPoints: allPanelists.reduce((sum, p) => sum + (p.total_points || 0), 0)
                });
            }
        } catch (error) {
            console.error('Error fetching panelists:', error);
            showSnackbar('Error loading panelists', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchTerm, statusFilter]);

    useEffect(() => {
        fetchPanelists();
    }, [fetchPanelists]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const resetForm = () => {
        setPanelistForm({
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
            demographics: {
                age: '',
                gender: '',
                location: '',
                occupation: '',
                education: '',
                income_range: ''
            }
        });
    };

    const handleCreatePanelist = async () => {
        if (!panelistForm.email.trim()) {
            showSnackbar('Email is required', 'error');
            return;
        }
        
        try {
            const response = await fetch('/api/panelists', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(panelistForm)
            });
            
            if (response.ok) {
                showSnackbar('Panelist added successfully');
                setCreateDialog(false);
                resetForm();
                fetchPanelists();
            } else {
                const error = await response.json();
                showSnackbar(error.message || 'Error creating panelist', 'error');
            }
        } catch (error) {
            console.error('Error creating panelist:', error);
            showSnackbar('Error creating panelist', 'error');
        }
    };

    const handleUpdatePanelist = async () => {
        try {
            const response = await fetch(`/api/panelists/${selectedPanelist.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(panelistForm)
            });
            
            if (response.ok) {
                showSnackbar('Panelist updated successfully');
                setEditDialog(false);
                resetForm();
                fetchPanelists();
            } else {
                const error = await response.json();
                showSnackbar(error.message || 'Error updating panelist', 'error');
            }
        } catch (error) {
            console.error('Error updating panelist:', error);
            showSnackbar('Error updating panelist', 'error');
        }
    };

    const handleDeletePanelist = async (panelistId) => {
        if (!window.confirm('Are you sure you want to delete this panelist?')) return;
        
        try {
            const response = await fetch(`/api/panelists/${panelistId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                showSnackbar('Panelist deleted');
                fetchPanelists();
            } else {
                const error = await response.json();
                showSnackbar(error.message || 'Error deleting panelist', 'error');
            }
        } catch (error) {
            console.error('Error deleting panelist:', error);
            showSnackbar('Error deleting panelist', 'error');
        }
    };

    const handleAwardPoints = async () => {
        if (rewardForm.points <= 0) {
            showSnackbar('Points must be greater than 0', 'error');
            return;
        }
        
        try {
            const response = await fetch('/api/panelists/points', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    panelist_id: selectedPanelist.id,
                    points: rewardForm.points,
                    reason: rewardForm.reason
                })
            });
            
            if (response.ok) {
                showSnackbar(`${rewardForm.points} points awarded`);
                setRewardDialog(false);
                setRewardForm({ points: 0, reason: '' });
                fetchPanelists();
            } else {
                const error = await response.json();
                showSnackbar(error.message || 'Error awarding points', 'error');
            }
        } catch (error) {
            console.error('Error awarding points:', error);
            showSnackbar('Error awarding points', 'error');
        }
    };

    const openEditDialog = (panelist) => {
        setSelectedPanelist(panelist);
        setPanelistForm({
            email: panelist.email,
            first_name: panelist.first_name || '',
            last_name: panelist.last_name || '',
            phone: panelist.phone || '',
            demographics: panelist.demographics || {
                age: '',
                gender: '',
                location: '',
                occupation: '',
                education: '',
                income_range: ''
            }
        });
        setEditDialog(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'success';
            case 'INACTIVE': return 'default';
            case 'BLACKLISTED': return 'error';
            case 'PENDING': return 'warning';
            default: return 'default';
        }
    };

    const renderPanelistForm = () => (
        <>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="First Name"
                        value={panelistForm.first_name}
                        onChange={(e) => setPanelistForm({ ...panelistForm, first_name: e.target.value })}
                        margin="normal"
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Last Name"
                        value={panelistForm.last_name}
                        onChange={(e) => setPanelistForm({ ...panelistForm, last_name: e.target.value })}
                        margin="normal"
                    />
                </Grid>
            </Grid>
            
            <TextField
                fullWidth
                label="Email"
                type="email"
                value={panelistForm.email}
                onChange={(e) => setPanelistForm({ ...panelistForm, email: e.target.value })}
                required
                margin="normal"
            />
            
            <TextField
                fullWidth
                label="Phone"
                value={panelistForm.phone}
                onChange={(e) => setPanelistForm({ ...panelistForm, phone: e.target.value })}
                margin="normal"
            />
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Demographics</Typography>
            
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Age"
                        type="number"
                        value={panelistForm.demographics.age}
                        onChange={(e) => setPanelistForm({
                            ...panelistForm,
                            demographics: { ...panelistForm.demographics, age: e.target.value }
                        })}
                        margin="normal"
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Gender</InputLabel>
                        <Select
                            value={panelistForm.demographics.gender}
                            label="Gender"
                            onChange={(e) => setPanelistForm({
                                ...panelistForm,
                                demographics: { ...panelistForm.demographics, gender: e.target.value }
                            })}
                        >
                            <MenuItem value="">Not specified</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="non-binary">Non-binary</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            
            <TextField
                fullWidth
                label="Location"
                value={panelistForm.demographics.location}
                onChange={(e) => setPanelistForm({
                    ...panelistForm,
                    demographics: { ...panelistForm.demographics, location: e.target.value }
                })}
                margin="normal"
                placeholder="City, Country"
            />
            
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Occupation"
                        value={panelistForm.demographics.occupation}
                        onChange={(e) => setPanelistForm({
                            ...panelistForm,
                            demographics: { ...panelistForm.demographics, occupation: e.target.value }
                        })}
                        margin="normal"
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Education</InputLabel>
                        <Select
                            value={panelistForm.demographics.education}
                            label="Education"
                            onChange={(e) => setPanelistForm({
                                ...panelistForm,
                                demographics: { ...panelistForm.demographics, education: e.target.value }
                            })}
                        >
                            <MenuItem value="">Not specified</MenuItem>
                            <MenuItem value="high_school">High School</MenuItem>
                            <MenuItem value="some_college">Some College</MenuItem>
                            <MenuItem value="bachelors">Bachelor's Degree</MenuItem>
                            <MenuItem value="masters">Master's Degree</MenuItem>
                            <MenuItem value="doctorate">Doctorate</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            
            <FormControl fullWidth margin="normal">
                <InputLabel>Income Range</InputLabel>
                <Select
                    value={panelistForm.demographics.income_range}
                    label="Income Range"
                    onChange={(e) => setPanelistForm({
                        ...panelistForm,
                        demographics: { ...panelistForm.demographics, income_range: e.target.value }
                    })}
                >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="under_25k">Under $25,000</MenuItem>
                    <MenuItem value="25k_50k">$25,000 - $50,000</MenuItem>
                    <MenuItem value="50k_75k">$50,000 - $75,000</MenuItem>
                    <MenuItem value="75k_100k">$75,000 - $100,000</MenuItem>
                    <MenuItem value="100k_150k">$100,000 - $150,000</MenuItem>
                    <MenuItem value="over_150k">Over $150,000</MenuItem>
                </Select>
            </FormControl>
        </>
    );

    if (loading && panelists.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigate('/surveys')} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            Panel Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage your survey panelists and rewards
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Upload />}
                    >
                        Import CSV
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => {
                            resetForm();
                            setCreateDialog(true);
                        }}
                    >
                        Add Panelist
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Person sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.total}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Panelists
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Star sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.active}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <EmojiEvents sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{stats.totalPoints.toLocaleString()}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Points Awarded
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <AttachMoney sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                        ${(stats.totalPoints * 0.01).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Est. Rewards Value
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name, email, or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {Object.entries(PANELIST_STATUS_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Button 
                            startIcon={<Download />} 
                            variant="outlined"
                            fullWidth
                        >
                            Export
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Panelists Table */}
            <Paper>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Panelist</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Demographics</TableCell>
                                <TableCell>Surveys</TableCell>
                                <TableCell>Points</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {panelists.map((panelist) => (
                                <TableRow key={panelist.id} hover>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Avatar sx={{ mr: 2 }}>
                                                {(panelist.first_name?.[0] || panelist.email[0]).toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {panelist.first_name} {panelist.last_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Joined {new Date(panelist.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">{panelist.email}</Typography>
                                            {panelist.phone && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {panelist.phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            {panelist.demographics?.age && (
                                                <Chip 
                                                    icon={<Cake />} 
                                                    label={`${panelist.demographics.age} yrs`}
                                                    size="small"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            )}
                                            {panelist.demographics?.location && (
                                                <Chip 
                                                    icon={<LocationOn />} 
                                                    label={panelist.demographics.location}
                                                    size="small"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {panelist.surveys_completed || 0} completed
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Star sx={{ color: 'warning.main', mr: 0.5 }} />
                                            {(panelist.total_points || 0).toLocaleString()}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PANELIST_STATUS_LABELS[panelist.status] || panelist.status}
                                            color={getStatusColor(panelist.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Award Points">
                                            <IconButton 
                                                size="small"
                                                onClick={() => {
                                                    setSelectedPanelist(panelist);
                                                    setRewardDialog(true);
                                                }}
                                            >
                                                <EmojiEvents />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Survey">
                                            <IconButton size="small">
                                                <Send />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton 
                                                size="small"
                                                onClick={() => openEditDialog(panelist)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton 
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeletePanelist(panelist.id)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {panelists.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="text.secondary" py={4}>
                                            No panelists found. Add panelists to start building your research panel.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* Create Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add New Panelist</DialogTitle>
                <DialogContent>
                    {renderPanelistForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreatePanelist}>
                        Add Panelist
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Panelist</DialogTitle>
                <DialogContent>
                    {renderPanelistForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdatePanelist}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Award Points Dialog */}
            <Dialog open={rewardDialog} onClose={() => setRewardDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Award Points</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Award points to: <strong>{selectedPanelist?.first_name} {selectedPanelist?.last_name}</strong>
                    </Typography>
                    <Typography variant="body2" mb={2}>
                        Current balance: <Star sx={{ fontSize: 16, verticalAlign: 'middle', color: 'warning.main' }} /> 
                        {(selectedPanelist?.total_points || 0).toLocaleString()} points
                    </Typography>
                    
                    <TextField
                        fullWidth
                        label="Points to Award"
                        type="number"
                        value={rewardForm.points}
                        onChange={(e) => setRewardForm({ ...rewardForm, points: parseInt(e.target.value) || 0 })}
                        margin="normal"
                        inputProps={{ min: 1 }}
                    />
                    
                    <TextField
                        fullWidth
                        label="Reason"
                        value={rewardForm.reason}
                        onChange={(e) => setRewardForm({ ...rewardForm, reason: e.target.value })}
                        margin="normal"
                        placeholder="e.g., Survey completion bonus, Referral reward"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRewardDialog(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<EmojiEvents />}
                        onClick={handleAwardPoints}
                    >
                        Award Points
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PanelistManagement;